import { useAuthStore } from '@/store/authStore';

/**
 * Hook centralizado de permissões por cargo.
 * Usar em TODAS as telas para esconder/mostrar UI baseado em cargo.
 * 
 * Hierarquia:
 * - super_admin: tudo liberado
 * - tenant_admin: tudo do próprio tenant (não pode criar super_admin)
 * - gestor: só própria loja, não pode editar pares, só cria vendedor
 * - vendedor: só vê próprio escopo, sem relatórios/exportar
 */
export function useRolePermissions() {
  const profile = useAuthStore((state: any) => state.profile);
  const userObj = useAuthStore((state: any) => state.user);
  const user = profile || userObj;
  const role = user?.role || '';
  
  const isSuperAdmin = role === 'super_admin';
  const isTenantAdmin = role === 'tenant_admin';
  const isGestor = role === 'gestor';
  const isVendedor = role === 'vendedor';
  
  // Permissões efetivas (do DB ou default por cargo)
  const permissions = profile?.permissions || {};
  const hasPerm = (perm: string): boolean => {
    if (isSuperAdmin || isTenantAdmin) return true; // admins têm tudo
    return permissions[perm] === true;
  };
  
  // Roles que podem ser criados pelo usuário logado
  const getCreatableRoles = (): { value: string; label: string }[] => {
    if (isSuperAdmin) return [
      { value: 'tenant_admin', label: 'Dono' },
      { value: 'gestor', label: 'Gerente' },
      { value: 'vendedor', label: 'Vendedor' }
    ];
    if (isTenantAdmin) return [
      { value: 'gestor', label: 'Gerente' },
      { value: 'vendedor', label: 'Vendedor' }
    ];
    if (isGestor) return [
      { value: 'vendedor', label: 'Vendedor' }
    ];
    return []; // vendedor não cria ninguém
  };
  
  // Pode ver a aba de Relatórios?
  const canViewReports = hasPerm('can_view_reports');
  
  // Pode exportar dados?
  const canExportData = hasPerm('can_export_data');
  
  // Pode importar dados?
  const canImportData = hasPerm('can_import_leads');
  
  // Pode deletar leads?
  const canDeleteLeads = hasPerm('can_delete_leads');
  
  // Pode atribuir leads?
  const canAssignLeads = hasPerm('can_assign_leads');
  
  // Pode gerenciar usuários (criar/editar)?
  const canManageUsers = isSuperAdmin || isTenantAdmin || isGestor;
  
  // Pode criar usuário com role específico?
  const canCreateRole = (targetRole: string): boolean => {
    return getCreatableRoles().some(r => r.value === targetRole);
  };
  
  // Pode editar usuário com role específico?
  const canEditRole = (targetRole: string): boolean => {
    if (isSuperAdmin) return true;
    if (isTenantAdmin) return targetRole !== 'super_admin';
    if (isGestor) return targetRole === 'vendedor'; // só edita vendedor
    return false; // vendedor não edita ninguém
  };
  
  // Pode ver todos os leads do tenant (não só os seus)?
  const canViewAllLeads = isSuperAdmin || isTenantAdmin || hasPerm('can_view_all_leads');
  
  // Pode editar Kanban (colunas)?
  const canEditKanban = hasPerm('can_edit_kanban');
  
  // Pode configurar WhatsApp?
  const canConfigWhatsApp = hasPerm('can_config_whatsapp');
  
  // Pode gerenciar respostas rápidas?
  const canManageCannedResponses = hasPerm('can_manage_canned_responses');
  
  return {
    role,
    isSuperAdmin,
    isTenantAdmin,
    isGestor,
    isVendedor,
    getCreatableRoles,
    canCreateRole,
    canEditRole,
    canViewReports,
    canExportData,
    canImportData,
    canDeleteLeads,
    canAssignLeads,
    canManageUsers,
    canViewAllLeads,
    canEditKanban,
    canConfigWhatsApp,
    canManageCannedResponses,
    hasPerm,
  };
}
